import isPlainObject from "lodash/isPlainObject";

export enum SortingType {
  Name,
}

export const SortingOption = {
  [SortingType.Name]: "name",
};

export const DISCOVER_APPS_TAG = "Discover Apps";
export const SPOTLIGHT_APPS_TAG = "Spotlight App";
export const POPULAR_APPS_TAG = "Popular Pick App";
export const ADDITIONAL_INFO_PRO = "Included with Pro & Enterprise plans";

export const APP_STORE_DISCOVER_PATH_NANE = "/apps-store/category/discover/";
export const DISCOVER_APPS_CATEGORY_ID = "discover";
export const ALL_APPS_CATEGORY_ID = "all apps";
export const PREMIUM_APPS_CATEGORY_ID = "premium";

// The app name maps to the readableId from Project Parity {[appName]: [prismic-slug]}
// ⚠️ WARNING: If you change an app's slug in Prismic, you MUST update this mapping accordingly.
// Otherwise, the App Store will fail to match Prismic content with Studio apps and will fallback to DB values or empty values.
const BASE_APP_NAME_MAPPING = {
  "Air Quality": "air-quality",
  Awardco: "awardco",
  "Ask a Question": "ask-a-question",
  Bonusly: "bonusly",
  "Building Directory": "building-directory",
  "Business & Finance News": "business-and-finance-news",
  "Calendar for Google": "googlecalendar",
  Canva: "canva",
  Canvas: "canvas",
  Clock: "clock",
  "Count Up Timer": "count-up",
  "Countdown Timer": "count-down",
  Currencies: "currencies",
  Date: "date",
  "Digital Menu Board": "digital-menu-board",
  "Digital Signs": "digital-signs",
  "Dropbox Gallery": "dropbox-gallery",
  "Embed App": "embed",
  "Emergency Alerts": "emergency-alerts",
  Engage: "engage",
  "Entertainment & Lifestyle News": "entertainment-news",
  Environment: "environment",
  "Facebook Recommendations": "facebook-recommendations",
  "Flight Schedules": "flight-schedules",
  Firstup: "first-up",
  "Follow Us": "follow-us",
  "Gallery for Instagram Business": "instagram-business",
  "Gallery for Instagram": "instagram",
  GeckoBoard: "geckoboard",
  "Google Alerts": "google-alerts",
  "Google Reviews": "google-reviews",
  "Google Sheets": "google-sheets",
  "Google Slides": "google-slides",
  "Google Traffic": "google-traffic",
  "Google Trends": "google-trends",
  "Headline News": "news",
  "Inspirational Quotes": "inspirational-quotes",
  "Live News": "live-news",
  Lumapps: "lumapps",
  "Meet The Team": "meet-the-team",
  "Meeting Room": "meeting-room",
  "Microsoft Excel": "microsoft-excel",
  "Microsoft OneDrive": "onedrive",
  "Microsoft Outlook Calendar": "outlook-calendar",
  "Microsoft Power BI": "microsoftpowerbi1",
  "Microsoft SharePoint": "microsoft-sharepoint",
  "Microsoft Teams": "microsoft-teams",
  "Microsoft Teams Rooms": "microsoft-teams-rooms",
  "Microsoft Viva Engage": "microsoft-viva-engage",
  "Microsoft Yammer (Legacy app)": "yammer",
  Monday: "monday",
  "Natural World News": "natural-world-news",
  "Noticeboard 2.0": "noticeboard",
  "On This Day": "on-this-day",
  "Follow Us On Facebook": "facebook-page-likes",
  "Page Scroll": "pagescroll",
  Playgrounds: "playgrounds",
  "Poster My Wall": "poster-my-wall",
  "Quick Post": "quick-post",
  Quote: "quote",
  "RSS Feed": "rss-feed",
  "ScreenCloud Broadcast": "live-streams",
  Simpplr: "simpplr",
  Slack: "slack",
  "Soccer Scores": "soccer-scores",
  "Business Feed For Facebook": "facebook-social-feed",
  "Sports Live Scores": "sports-live-scores",
  "Sports News": "sports-news",
  Staffbase: "staffbase",
  "Staffbase News": "staffbasenews",
  Stocks: "stocks",
  "Strea.ma": "strea-ma",
  "Taggbox Display": "taggbox-display",
  // "Team News Uploader", ??
  "Team News": "team-news-self-serve",
  "Team News (Legacy)": "team-news",
  "Tech News": "technology-news",
  Transport: "transport",
  TripAdvisor: "trip-advisor",
  Unily: "unily",
  Vengo: "vengo",
  Vimeo: "vimeo",
  "Walls.io": "walls-io",
  Weather: "weather",
  Webhooks: "webhooks",
  Wordpress: "wordpress",
  "World Clock": "world-clock",
  Yelp: "yelp-reviews",
  "YouTube Live": "youtube-live",
  YouTube: "youtube",
  "Zendesk Reports": "zendesk",
  "Zoom Rooms": "zoom-rooms",
} as const;

/**
 * Reads app name to Prismic slug overrides from localStorage.
 * Used for testing - allows overriding slugs without changing the base config.
 *
 * localStorage format:
 * { "prismicAppSlugOverrides": { "App Name": "new-prismic-slug", "Another App": "another-slug" } }
 */
function getAppSlugOverrides(): Partial<
  Record<keyof typeof BASE_APP_NAME_MAPPING, string>
> {
  if (typeof window === "undefined") return {};

  try {
    const overridesJson = localStorage.getItem("prismicAppSlugOverrides");
    if (!overridesJson) return {};
    const overrides = JSON.parse(overridesJson);
    // Validate that it's a plain object
    if (isPlainObject(overrides)) {
      return overrides;
    }
  } catch (error) {
    console.warn(
      "Failed to parse prismicAppSlugOverrides from localStorage:",
      error,
    );
  }

  return {};
}

/**
 * Merged app name mapping with localStorage overrides.
 * Overrides from localStorage take precedence for testing purposes.
 */
export const APP_NAME_MAPPING: typeof BASE_APP_NAME_MAPPING = {
  ...BASE_APP_NAME_MAPPING,
  ...getAppSlugOverrides(),
} as typeof BASE_APP_NAME_MAPPING;

export function getAppSlug(appName: string): string | undefined {
  return APP_NAME_MAPPING[appName as keyof typeof APP_NAME_MAPPING];
}
